import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import { getQueryParamsFromFilters } from '@/system-logs/utils/get-query-params-from-filters';
import type { Filters } from '@/system-logs/view/state/selected-filters';

export namespace GetLogCountQuery {
  export const key = (input: Input) => [
    'system-logs',
    'log-count',
    ...Object.entries(input).flatMap(([key, value]) => [key, value]),
  ];

  export type Input = {
    siteAccureId?: string;
    unitAccureId?: string;
    from?: Date;
    to?: Date;
    filters?: Filters;
  };

  export const OutputSchema = z.object({ count: z.number().nonnegative() });
  export type Output = z.infer<typeof OutputSchema>;

  function useQueryFn() {
    const systemLogsList = useInjectFeApiContext(
      FeApi.systemLogsV1LogsListRetrieve,
    );
    return async function queryFn(input: Input) {
      const { siteAccureId, unitAccureId, from, to, filters } = input;

      const queryParams = getQueryParamsFromFilters({
        systemId: siteAccureId,
        filters,
      });

      const res = await systemLogsList({
        ...queryParams,
        ...(!queryParams.unitAccureId && unitAccureId ? { unitAccureId } : {}),
        startDate: from?.getTime().toString(),
        endDate: to?.getTime().toString(),
      });
      if (res.status !== 200) {
        return { count: 0 };
      }
      return OutputSchema.parse({ count: res.data.count });
    };
  }

  export function useQuery(input: Input) {
    const queryFn = useQueryFn();

    /**
     * We do no frontend caching for this query because it is already done in the backend.
     */
    return rq.useQuery(key(input), () => queryFn(input), {
      refetchInterval: false,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();

    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
